import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Users, Trash2, Shield, Eye, Edit, Mail, KeyRound, Grid3X3, List, UserCheck, Copy } from 'lucide-react';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useSettings } from '@/contexts/SettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Utilisateurs = ({ user }) => {
  const { isMobile } = useScreenSize();
  const { settings } = useSettings();
  const itemsPerPage = useMemo(() => settings.items_per_page || 12, [settings.items_per_page]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterRole, setFilterRole] = useState('tous');
  const [sortBy, setSortBy] = useState('nom');
  
  const [inviteFormData, setInviteFormData] = useState({
    email: '',
    nom: '',
    role: 'viewer',
  });
  const [editFormData, setEditFormData] = useState({
    role: 'viewer',
    permissions: [],
  });
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [activatedUser, setActivatedUser] = useState(null);

  const isAdmin = user?.role === 'admin';
  const canWrite = user?.permissions?.includes('write') || user?.role === 'admin';
  const canDelete = user?.permissions?.includes('delete') || user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);

  // Basculer automatiquement en mode cartes sur mobile
  useEffect(() => {
    if (isMobile && viewMode === 'table') {
      setViewMode('cards');
    }
  }, [isMobile, viewMode]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  }, []);

  const openInviteDialog = () => {
    setInviteFormData({
      email: '',
      nom: '',
      role: 'viewer',
    });
    setInviteDialogOpen(true);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!canWrite) {
      toast.error('Vous n\'avez pas la permission d\'inviter');
      return;
    }

    const permissions = getPermissionsByRole(inviteFormData.role);

    try {
      const response = await axios.post(`${API}/users/invite`, {
        ...inviteFormData,
        permissions,
      });
      
      if (response.data.email_sent) {
        toast.success('Invitation envoyée par email avec succès');
      } else {
        toast.success('Utilisateur invité (email non configuré)');
      }
      
      fetchUsers();
      setInviteDialogOpen(false);
      resetInviteForm();
    } catch (error) {
      console.error('Error inviting user:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'invitation');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/users/${selectedUser.id}`, {
        role: editFormData.role,
        permissions: editFormData.permissions,
      });
      toast.success('Utilisateur modifié avec succès');
      fetchUsers();
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error editing user:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDelete = async (userId) => {
    if (!canDelete) {
      toast.error('Vous n\'avez pas la permission de supprimer');
      return;
    }

    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      try {
        await axios.delete(`${API}/users/${userId}`);
        toast.success('Utilisateur supprimé avec succès');
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const handleActivate = async (userToActivate) => {
    try {
      const response = await axios.post(`${API}/users/activate/${userToActivate.id}`);
      setActivatedUser(response.data);
      setActivateDialogOpen(true);
      toast.success(`Compte de ${userToActivate.nom || userToActivate.email} activé`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'activation');
    }
  };


  const openEditDialog = (userToEdit) => {
    setSelectedUser(userToEdit);
    setEditFormData({
      role: userToEdit.role,
      permissions: userToEdit.permissions,
    });
    setEditDialogOpen(true);
  };

  const resetInviteForm = () => {
    setInviteFormData({
      email: '',
      nom: '',
      role: 'viewer',
    });
  };

  const handleResendInvitation = async (userId, userEmail) => {
    try {
      await axios.post(`${API}/users/resend-invitation`, { user_id: userId });
      toast.success(`Invitation renvoyée à ${userEmail}`);
    } catch (error) {
      console.error('Error resending invitation:', error);
      toast.error('Erreur lors du renvoi de l\'invitation');
    }
  };

  const handleResetUserPassword = async (userId, userEmail, userName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir réinitialiser le mot de passe de ${userName} (${userEmail}) ?`)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/users/reset-password/${userId}`);
      toast.success(`Email de réinitialisation envoyé à ${userEmail}`);
      
      if (!response.data.email_sent) {
        toast.warning('Email non envoyé - vérifiez votre configuration SMTP');
      }
    } catch (error) {
      console.error('Error resetting user password:', error);
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  const getPermissionsByRole = (role) => {
    switch (role) {
      case 'admin':
        return ['read', 'write', 'delete'];
      case 'editor':
        return ['read', 'write'];
      default:
        return ['read'];
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Shield className="w-5 h-5 text-red-600" />;
      case 'editor':
        return <Edit className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
      default:
        return <Eye className="w-5 h-5 text-gray-600" />;
    }
  };

  // Filtrage et tri
  const filteredUsers = useMemo(() => {
    let filtered = [...users];

    if (filterRole !== 'tous') {
      filtered = filtered.filter(u => u.role === filterRole);
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'nom':
          return (a.nom || '').localeCompare(b.nom || '');
        case 'email':
          return (a.email || '').localeCompare(b.email || '');
        case 'role':
          return (a.role || '').localeCompare(b.role || '');
        case 'date':
          return new Date(b.created_at) - new Date(a.created_at);
        default:
          return (a.nom || '').localeCompare(b.nom || '');
      }
    });

    return filtered;
  }, [users, filterRole, sortBy]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterRole, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + itemsPerPage);

  // Statistiques
  const adminCount = users.filter(u => u.role === 'admin').length;
  const editorCount = users.filter(u => u.role === 'editor').length;
  const viewerCount = users.filter(u => u.role === 'viewer').length;
  const pendingInvitations = users.filter(u => u.invitation_status === 'pending').length;

  const renderTableView = () => (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <CardTitle className="text-xl">Tableau des utilisateurs</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-200 dark:border-gray-800 dark:border-gray-800">
                <th className="text-left py-3 px-2 lg:px-4 font-semibold text-gray-700 text-xs lg:text-sm">Nom</th>
                <th className="text-left py-3 px-2 lg:px-4 font-semibold text-gray-700 text-xs lg:text-sm">Email</th>
                <th className="text-center py-3 px-2 lg:px-4 font-semibold text-gray-700 text-xs lg:text-sm hidden md:table-cell">Rôle</th>
                <th className="text-center py-3 px-2 lg:px-4 font-semibold text-gray-700 text-xs lg:text-sm hidden lg:table-cell">Statut</th>
                <th className="text-right py-3 px-2 lg:px-4 font-semibold text-gray-700 text-xs lg:text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Aucun utilisateur
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((u) => (
                  <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50 dark:hover:bg-gray-900">
                    <td className="py-3 px-2 lg:px-4">
                      <div className="flex items-center gap-2">
                        {getRoleIcon(u.role)}
                        <span className="font-medium text-gray-900 dark:text-white">{u.nom}</span>
                        {u.id === user.id && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Vous</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-2 lg:px-4 text-gray-600 text-xs lg:text-sm">
                      {u.email}
                    </td>
                    <td className="py-3 px-2 lg:px-4 text-center hidden md:table-cell">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        u.role === 'admin' ? 'bg-red-100 text-red-700' :
                        u.role === 'editor' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="py-3 px-2 lg:px-4 text-center hidden lg:table-cell">
                      {u.invitation_status === 'pending' ? (
                        <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs">En attente</span>
                      ) : (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Actif</span>
                      )}
                    </td>
                    <td className="py-3 px-1 lg:px-4 text-right">
                      {u.id !== user.id && (
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(u)}
                            className="text-blue-600 hover:bg-blue-50 p-1 lg:p-2"
                            title="Modifier"
                          >
                            <Edit className="w-3 h-3 lg:w-4 lg:h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResetUserPassword(u.id, u.email, u.nom)}
                            className="text-amber-600 hover:bg-amber-50 p-1 lg:p-2"
                            title="Reset mot de passe"
                          >
                            <KeyRound className="w-3 h-3 lg:w-4 lg:h-4" />
                          </Button>
                          {u.invitation_status === 'pending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResendInvitation(u.id, u.email)}
                              className="text-green-600 hover:bg-green-50 p-1 lg:p-2"
                              title="Renvoyer invitation"
                            >
                              <Mail className="w-3 h-3 lg:w-4 lg:h-4" />
                            </Button>
                          )}
                          {u.invitation_status === 'pending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleActivate(u)}
                              className="text-emerald-600 hover:bg-emerald-50 p-1 lg:p-2"
                              title="Activer le compte"
                              data-testid={`activate-user-${u.id}`}
                            >
                              <UserCheck className="w-3 h-3 lg:w-4 lg:h-4" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(u.id)}
                              className="text-red-600 hover:bg-red-50 p-1 lg:p-2"
                              title="Supprimer"
                            >
                              <Trash2 className="w-3 h-3 lg:w-4 lg:h-4" />
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-800 dark:border-gray-800">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Page {currentPage} sur {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Précédent
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                Suivant
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderCardsView = () => (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <CardTitle className="text-xl">Utilisateurs en cartes</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {paginatedUsers.length === 0 ? (
            <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400">
              <p>Aucun utilisateur</p>
            </div>
          ) : (
            paginatedUsers.map((u) => (
              <Card key={u.id} className="border hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getRoleIcon(u.role)}
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-white">{u.nom}</div>
                          {u.id === user.id && (
                            <span className="text-xs text-blue-600">(Vous)</span>
                          )}
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        u.role === 'admin' ? 'bg-red-100 text-red-700' :
                        u.role === 'editor' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {u.role}
                      </span>
                    </div>

                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {u.email}
                    </div>

                    {u.invitation_status === 'pending' && (
                      <div className="bg-amber-50 p-2 rounded border border-amber-200">
                        <div className="text-xs text-amber-600 font-medium">Invitation en attente</div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleActivate(u)}
                          className="mt-2 w-full text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                          data-testid={`activate-card-${u.id}`}
                        >
                          <UserCheck className="w-4 h-4 mr-2" />
                          Activer le compte
                        </Button>
                      </div>
                    )}

                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Inscrit le {new Date(u.created_at).toLocaleDateString('fr-FR')}
                    </div>

                    {u.id !== user.id && (
                      <div className="flex gap-2 pt-2 border-t border-gray-100">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(u)}
                          className="flex-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Modifier
                        </Button>
                        {canDelete && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(u.id)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Pagination pour cartes */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-800 dark:border-gray-800">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Page {currentPage} sur {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Précédent
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                Suivant
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="border-0 shadow-md p-8">
          <div className="text-center">
            <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès restreint</h2>
            <p className="text-gray-600 dark:text-gray-400">Cette section est réservée aux administrateurs.</p>
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 dark:border-slate-400"></div>
      </div>
    );
  }

  // Pagination logic définie plus haut

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* En-tête harmonisé */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Gestion des Utilisateurs</h1>
        </div>
        <div className="flex gap-2">
          {canWrite && (
            <Button
              onClick={openInviteDialog}
              className="bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600"
              data-testid="invite-user-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Inviter un utilisateur
            </Button>
          )}
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-md border-l-4 border-l-red-500">
          <CardContent className="pt-6">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-red-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Administrateurs</p>
                <p className="text-2xl font-bold text-red-600">{adminCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center">
              <Edit className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Éditeurs</p>
                <p className="text-2xl font-bold text-blue-600">{editorCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md border-l-4 border-l-gray-500">
          <CardContent className="pt-6">
            <div className="flex items-center">
              <Eye className="w-8 h-8 text-gray-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Lecteurs</p>
                <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">{viewerCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md border-l-4 border-l-amber-500">
          <CardContent className="pt-6">
            <div className="flex items-center">
              <Mail className="w-8 h-8 text-amber-600 dark:text-amber-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">En attente</p>
                <p className="text-2xl font-bold text-amber-600">{pendingInvitations}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtres et Options */}
      <Card className="border-0 shadow-md">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Filtrer par rôle</Label>
              <Select value={filterRole} onValueChange={setFilterRole}>
                <SelectTrigger data-testid="filter-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tous">Tous les rôles</SelectItem>
                  <SelectItem value="admin">Administrateurs</SelectItem>
                  <SelectItem value="editor">Éditeurs</SelectItem>
                  <SelectItem value="viewer">Lecteurs</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Trier par</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger data-testid="sort-users-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nom">Nom (A-Z)</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="role">Rôle</SelectItem>
                  <SelectItem value="date">Date inscription</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Affichage</Label>
              <div className="flex gap-1">
                {!isMobile && (
                  <Button
                    type="button"
                    variant={viewMode === 'table' ? 'default' : 'outline'}
                    size="icon"
                    onClick={() => setViewMode('table')}
                    title="Vue tableau"
                  >
                    <List className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  type="button"
                  variant={viewMode === 'cards' ? 'default' : 'outline'}
                  size="icon"
                  onClick={() => setViewMode('cards')}
                  title="Vue cartes"
                >
                  <Grid3X3 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Résultats</Label>
              <div className="h-10 flex items-center px-3 bg-gray-50 dark:bg-gray-950 rounded-lg border">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {filteredUsers.length} utilisateur(s) affiché(s)
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Affichage données */}
      {viewMode === 'table' && renderTableView()}
      {viewMode === 'cards' && renderCardsView()}

      {/* Dialog Invitation - Harmonisé */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Inviter un utilisateur</DialogTitle>
            <DialogDescription>Invitez une personne à rejoindre votre organisation</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Adresse email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={inviteFormData.email}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, email: e.target.value })}
                  required
                  placeholder="utilisateur@exemple.com"
                  data-testid="invite-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nom">Nom complet *</Label>
                <Input
                  id="nom"
                  value={inviteFormData.nom}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, nom: e.target.value })}
                  required
                  placeholder="Nom de la personne"
                  data-testid="invite-nom-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Rôle *</Label>
              <Select
                value={inviteFormData.role}
                onValueChange={(value) => setInviteFormData({ ...inviteFormData, role: value })}
              >
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">👁️ Lecteur (lecture seule)</SelectItem>
                  <SelectItem value="editor">✏️ Éditeur (lecture + écriture)</SelectItem>
                  <SelectItem value="admin">🛡️ Administrateur (tous droits)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                className="flex-1 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600"
                data-testid="invite-submit-button"
              >
                <Mail className="w-4 h-4 mr-2" />
                Envoyer l'invitation
              </Button>
              <Button type="button" variant="outline" onClick={() => setInviteDialogOpen(false)}>
                Annuler
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Modification - Harmonisé */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier l'utilisateur</DialogTitle>
            <DialogDescription>Modifiez le rôle et les permissions de {selectedUser?.nom}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-role">Rôle *</Label>
              <Select
                value={editFormData.role}
                onValueChange={(value) => setEditFormData({ 
                  ...editFormData, 
                  role: value, 
                  permissions: getPermissionsByRole(value) 
                })}
              >
                <SelectTrigger data-testid="edit-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">👁️ Lecteur (lecture seule)</SelectItem>
                  <SelectItem value="editor">✏️ Éditeur (lecture + écriture)</SelectItem>
                  <SelectItem value="admin">🛡️ Administrateur (tous droits)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-blue-50 p-3 rounded">
              <p className="text-sm text-blue-700">
                <span className="font-medium">Permissions :</span> {editFormData.permissions.join(', ')}
              </p>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                className="flex-1 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600"
                data-testid="edit-submit-button"
              >
                <Edit className="w-4 h-4 mr-2" />
                Modifier l'utilisateur
              </Button>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Annuler
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Activate User Dialog - shows temp password */}
      <Dialog open={activateDialogOpen} onOpenChange={setActivateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Compte activé avec succès</DialogTitle>
            <DialogDescription>Transmettez ces identifiants à l'utilisateur</DialogDescription>
          </DialogHeader>
          {activatedUser && (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <UserCheck className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-green-800">{activatedUser.nom || activatedUser.email}</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-2 bg-white rounded border">
                    <div><div className="text-xs text-gray-500">Email</div><div className="font-mono text-sm">{activatedUser.email}</div></div>
                    <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(activatedUser.email); toast.success('Email copié'); }} data-testid="copy-email-btn"><Copy className="w-4 h-4" /></Button>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-white rounded border">
                    <div><div className="text-xs text-gray-500">Mot de passe temporaire</div><div className="font-mono text-sm font-bold">{activatedUser.temp_password}</div></div>
                    <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(activatedUser.temp_password); toast.success('Mot de passe copié'); }} data-testid="copy-password-btn"><Copy className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
              <div className="p-3 bg-amber-50 rounded border border-amber-200 text-xs text-amber-700">
                L'utilisateur devra changer son mot de passe lors de sa première connexion.
              </div>
              <Button className="w-full" onClick={() => setActivateDialogOpen(false)} data-testid="close-activate-dialog">Fermer</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Utilisateurs;